import { CommonPaginationResult } from "common/contracts/pagination.contracts";

import type { StoreObject, TypePolicies } from "@apollo/client";
import type {
  FieldMergeFunction,
  KeyArgsFunction,
} from "@apollo/client/cache/inmemory/policies";

/**
 * First occurrence per cache reference wins, in order: what lodash
 * `uniqBy(items, "__ref")` did before WHO-5479 moved it out of the shell.
 */
const uniqueByRef = <T>(items: T[]): T[] => {
  const seen = new Set<string | undefined>();

  return items.filter(item => {
    const ref = (item as { __ref?: string }).__ref;

    if (seen.has(ref)) {
      return false;
    }

    seen.add(ref);

    return true;
  });
};

/**
 * Function that provides a default behaviour for merging multiple pagination results.
 * Appends the incoming items to the existing items and uses the pagination data from the incoming result as the newest one.
 * @param existing - The existing pagination result that is already saved in the local Apollo cache.
 * @param incoming - The incoming pagination result that should be merged with the existing data in the cache.
 * @returns Merged pagination data.
 */
const defaultPaginationMerge = (
  existing: CommonPaginationResult | undefined,
  incoming: CommonPaginationResult
): CommonPaginationResult => {
  let existingItems: unknown[] = [];
  if (existing) {
    existingItems = existing.items;
  }
  const { items: incomingItems, pagination } = incoming;
  const mergedItems = existingItems.concat(incomingItems);
  return { items: uniqueByRef(mergedItems), pagination };
};

/**
 * Keys a field on the arguments that are set, and only those (WHO-5142 review). With the display
 * currency off, `buy_now_price(currency: $currency)` gets `currency: undefined`, which Apollo
 * would store as `buy_now_price({})`, apart from the `buy_now_price` the seller's own fragments
 * read and update. A seller's price edit would then not reach the product page until a reload.
 * Keyed this way, an unset currency stores as `buy_now_price` and a set one as its own entry.
 */
const setArgumentsOnly: KeyArgsFunction = (args, { fieldName }) => {
  const set = Object.keys(args ?? {}).filter(
    name => args?.[name] !== undefined
  );

  // An empty list would still store as `buy_now_price:{}`; the bare name is the shared entry.
  return set.length > 0 ? set : fieldName;
};

/** The fields that take the display currency (`currency: $currency`), as decision 13 put it. */
const displayCurrencyField = { keyArgs: setArgumentsOnly };

/**
 * A list of objects without an id whose items hold a converted price (`price(currency:
 * $currency)`). Apollo replaces such a list wholesale on every write, so when the euro and the
 * converted product land in one cache (the product page's server render, WHO-6199), the second
 * write drops the first one's `price` variant and that query's read comes back incomplete: a
 * `?currency=GBP` product page then rendered without its product. Merged item by item instead,
 * matched on `matchField`, so both variants stay. An item whose match field is missing replaces
 * nothing.
 */
const mergeItemsBy =
  (matchField: string): FieldMergeFunction<StoreObject[]> =>
  (existing, incoming, { readField, mergeObjects }) => {
    if (!existing) {
      return incoming;
    }

    return incoming.map(item => {
      const key = readField<string>(matchField, item);

      if (key === undefined) {
        return item;
      }

      const earlier = existing.find(
        candidate => readField<string>(matchField, candidate) === key
      );

      return earlier ? mergeObjects(earlier, item) : item;
    });
  };

/**
 * A field policy specifies how a particular field in our InMemoryCache is read and written.
 * We can define a field policy to merge the results of paginated queries into a single list.
 * Take a look at the official documentation for more info: https://www.apollographql.com/docs/react/pagination/core-api/#merging-paginated-results
 * @param keyArgs - Specifies which of the field's arguments cause the cache to store a separate value for each unique combination of those arguments.
 * @param merge - A merge function tells the Apollo Client cache how to combine incoming data with existing cached data for a particular field. Without this function, incoming field values overwrite existing field values by default.
 */
export const typePolicies = {
  Product: {
    fields: {
      buy_now_price: displayCurrencyField,
      original_price: displayCurrencyField,
      newPrice: displayCurrencyField,
      custom_shipping_cost: displayCurrencyField,
      /* Embedded in the product, never normalised: see `ExternalShippingMethod` below. */
      externalShippingMethods: { merge: mergeItemsBy("title") },
      shipping_method_prices: { merge: mergeItemsBy("country") },
    },
  },
  Auction: {
    fields: {
      buy_now_price: displayCurrencyField,
      minimum_bid: displayCurrencyField,
      sold_at: displayCurrencyField,
    },
  },
  ExternalShippingMethod: {
    /*
     * Its `id` is the shipping method's, shared by every product, while the price is the product's
     * own: normalised on it, every product would show one product's Brenger price. Stays embedded
     * even once a query selects `id` (Vladimir's review, WHO-6199).
     */
    keyFields: false,
    fields: { price: displayCurrencyField },
  },
  ShippingMethodCountryPrice: {
    fields: { price: displayCurrencyField },
  },
  Query: {
    fields: {
      getThreads: {
        keyArgs: [],
        merge: defaultPaginationMerge,
      },
      getMessages: {
        // Whenever the `threadId` field of the `input` argument changes or the `$lang` variable changes, store it as a separate value in cache.
        keyArgs: ["input", ["threadId"], "$lang"],
        merge: defaultPaginationMerge,
      },
    },
  },
} satisfies TypePolicies;
